/**
 * Responsibility: describe the theme park biome — level ground, with a complete fairground on it here and there.
 *
 * ## Built twice, and the second version is the shape of the thing
 *
 * The first version filled the region with a block grid, the way the city and the industrial estate do: a
 * landmark ride on a coarse lattice, avenues of stalls between, and small rides rolled per block. Flown, what
 * that produced at the edges of a region was **an entrance arch on its own in a field, and a shooting gallery
 * a few hundred units away from nothing**. For a city that is fine — one tower is still a building. A
 * fairground is not a thing that comes in pieces: an entrance with nothing behind it is not a small park, it is
 * a mistake.
 *
 * So a park is now a **site**, not a fill. One per lattice cell that wins its roll, and each one holds:
 *
 * - **One of every ride**, in a three-by-three core. Nine slots, nine distinct rides — so the layout is a
 *   shuffle rather than a set of rolls, and a park can never be missing its wheel.
 * - **A few extra small pieces** ringing the core: arches, stalls, a hall. This is where the variety lives, and
 *   it is somewhere it cannot strand anything, because every one of them is within sight of the wheel.
 *
 * The ground between sites is this biome's own flat terrain. A region is therefore mostly open ground with two
 * or three complete fairgrounds in it, which is what a fairground actually is — a place you come across.
 *
 * ## Why the whole park turns as one
 *
 * Slots are laid out in the site's own rotated frame and every ride takes the park's facing. A park is a
 * designed place rather than a scatter, and rides at independent angles read as debris. The variety comes from
 * the shuffle and the fringe, not from spinning each ride separately.
 *
 * ## What guarantees nothing overlaps
 *
 * A slot is 360 units and the widest ride reaches 167 from its own centre, so every ride fits inside its slot
 * with room to spare — and slots do not overlap. That is the whole of it, because **there is no collision
 * checking anywhere in this project**. A test measures the fit rather than trusting it.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createFbm, toUnitRange } from '../noise.js';
import { createLocalRng, deriveSeed } from '../rng.js';
import { getParkShapeOrDefault, PARK_SHAPES } from '../parkShapes.js';
import { PARK, PALETTE, PARK_PALETTE } from '../../config.js';

/** Half the width of a park's core, in world units. */
const CORE_HALF = (PARK.SLOTS_PER_AXIS * PARK.SLOT_SIZE) / 2;

/**
 * The peak neighbourhood, in lattice cells.
 *
 * Derived rather than configured, because the thing worth stating is a distance — "no two parks closer than
 * this" — and how many candidate cells that spans is an implementation detail of the lattice. Rounded up, so the
 * guarantee is never weaker than `PARK.PEAK_REACH` says it is.
 */
const PEAK_CELLS = Math.ceil(PARK.PEAK_REACH / PARK.SITE_SPACING);

/**
 * How far a park reaches from its centre, in world units.
 *
 * The fringe pieces sit furthest out, so this is what the siting checks have to cover and what the reservation
 * has to publish. Exported because both of those and the tests all need the same number.
 */
export const PARK_REACH = CORE_HALF * PARK.FRINGE_REACH + PARK.SLOT_SIZE / 2;

/**
 * The rides every park gets, one each.
 *
 * **Derived from the registry rather than listed**, so adding a ride is caught by the slot-count test instead of
 * quietly leaving the new one unbuildable.
 *
 * The two coasters are excluded here because they are *alternatives* — the same kind of thing built two ways —
 * and a park takes one of them. That is also what makes nine rides fit nine slots exactly.
 */
const CORE_FIXED = PARK_SHAPES.map((shape) => shape.id).filter(
  (id) => !PARK.COASTER_VARIANTS.includes(id),
);

/** How many rides a park's core holds: the fixed set plus whichever coaster it drew. */
const CORE_COUNT = CORE_FIXED.length + 1;

/** Linear interpolation, for turning a 0..1 roll into a range. */
function lerp(from, to, t) {
  return from + (to - from) * t;
}

/** An integer in a range, from a 0..1 roll. */
function countFrom(roll, min, max) {
  return min + Math.min(max - min, Math.floor(roll * (max - min + 1)));
}

/**
 * Where a site's centre sits, given its lattice cell.
 *
 * Jittered inside the cell so the lattice never shows as a grid, and derived from the cell index alone so every
 * tile that can see this park agrees about where it is.
 *
 * Draws exactly two values, and both callers depend on that: placement carries on down the same stream for the
 * park's facing and layout, so anything added here would shift every park in the world.
 */
function siteCentre(cellX, cellZ, random) {
  const spacing = PARK.SITE_SPACING;
  const wander = spacing * PARK.SITE_JITTER;

  return [
    (cellX + 0.5) * spacing + lerp(-wander, wander, random()),
    (cellZ + 0.5) * spacing + lerp(-wander, wander, random()),
  ];
}

/**
 * A strict order on cells, for breaking an exact tie in suitability.
 *
 * Two cells holding the identical float is vanishingly unlikely, but "unlikely" is not good enough: the
 * one-park-per-region guarantee rests on the winner being the maximum of a *strict total order*, and a
 * non-strict comparison would let two neighbouring cells each be a maximum and put two parks side by side —
 * which is the exact bug this version exists to fix.
 */
function cellPrecedes(aX, aZ, bX, bZ) {
  return aX !== bX ? aX < bX : aZ < bZ;
}

/**
 * Finds which lattice cells hold a park, shared by placement and by the reservation.
 *
 * One home for the rule, because the two have to agree: the reservation tells every other biome where to keep
 * out, so a reservation guarding a different set of cells than the one that gets built would leave real parks
 * with trees in them.
 *
 * ## One park per region, and how a lattice manages that
 *
 * The problem this solves is what was reported — flying over a fairground region and finding **several
 * complete fairgrounds side by side**. A lattice of candidate sites cannot fix that by itself, because a
 * lattice knows nothing about where one region ends.
 *
 * What does know is the biome's own suitability field. A region *is* a patch where that field leads its
 * rivals, and a smooth field over a patch has a peak. So a cell holds the park only if it outranks every cell
 * within `PEAK_REACH` of it. One peak, one park, and no code anywhere has to find the region's outline.
 *
 * **It has to be suitability and not weight.** Weight saturates — across the interior of a region the leader's
 * weight is exactly 1 over a wide area, so every candidate there ties and there is no unique winner. That was
 * the first attempt.
 *
 * **And the ranking has to be over cells that could actually build**, which was the second attempt's mistake.
 * See `scoreOf`.
 *
 * The neighbourhood also *is* the minimum distance between two parks: winners are more than `PEAK_REACH` apart
 * by construction. That is the "not right next door" guarantee, and unlike anything tuned it holds however the
 * field happens to be shaped.
 */
function createSiteFinder(seed) {
  const siteSeed = deriveSeed(seed, 'sites:park');

  /** A cell's jittered centre, from the cell index alone. */
  function centreOf(cellX, cellZ) {
    return siteCentre(cellX, cellZ, createLocalRng(siteSeed, cellX, cellZ));
  }

  /**
   * A cell's standing in the contest for its region's park, or `-Infinity` if it is not in the running.
   *
   * **Only cells where the biome actually leads are ranked, and getting that wrong cost most of the parks in the
   * world.** The first version ranked cells on raw suitability alone, which sounds equivalent and is not: the
   * *peak* of the fairground's own field can easily fall somewhere a rival is stronger still, and then every
   * candidate in the region next to it lost to a cell that could never build. Measured, 113 fairground regions
   * held 11 parks — nine in ten were empty flat ground.
   *
   * Requiring real presence is also what `MIN_SITE_WEIGHT` was always for. Elevation is a weighted sum, so at a
   * low park weight the ground under a site is mostly a neighbour's, and a neighbour can be a mountain.
   *
   * Memoised, and not as a nicety: the peak test reads a whole neighbourhood and neighbouring candidates share
   * nearly all of it, so without the memo a tile would sample the same coordinates hundreds of times.
   */
  function scoreOf(cellX, cellZ, { sampleWeight, sampleSuitability, cache }) {
    const key = `park:peak:${cellX},${cellZ}`;
    let value = cache?.get(key);

    if (value === undefined) {
      const [x, z] = centreOf(cellX, cellZ);

      value =
        sampleWeight(x, z) < PARK.MIN_SITE_WEIGHT ? Number.NEGATIVE_INFINITY : sampleSuitability(x, z);

      cache?.set(key, value);
    }

    return value;
  }

  /**
   * Whether a cell holds a park, judged on everything that needs no terrain.
   *
   * Split from the slope and clearance checks on purpose. The reservation has to answer from a context with no
   * ground sampler at all, so this is the part both callers can run, and placement adds the terrain gates on
   * top. That keeps the reservation a *superset* of the real parks, which is the safe direction to be wrong in.
   */
  function isCandidate(cellX, cellZ, context) {
    /*
      The centre is derived inside `scoreOf` rather than taken as an argument, even though placement already has
      it from its own stream. Both routes call `siteCentre` on a fresh stream for the cell so they cannot
      disagree, and paying for two extra rng draws is worth having the gate live in exactly one place — the
      reservation and the placement diverging is the failure that would put a tree inside a fairground.
    */
    const mine = scoreOf(cellX, cellZ, context);
    if (mine === Number.NEGATIVE_INFINITY) return false;

    for (let dx = -PEAK_CELLS; dx <= PEAK_CELLS; dx += 1) {
      for (let dz = -PEAK_CELLS; dz <= PEAK_CELLS; dz += 1) {
        if (dx === 0 && dz === 0) continue;

        const theirs = scoreOf(cellX + dx, cellZ + dz, context);

        if (theirs > mine) return false;
        if (theirs === mine && cellPrecedes(cellX + dx, cellZ + dz, cellX, cellZ)) return false;
      }
    }

    return true;
  }

  return { centreOf, isCandidate };
}

/**
 * The core layout: one ride to a slot, shuffled.
 *
 * **A shuffle rather than a roll per slot**, which is what guarantees one of everything. Rolling each slot
 * independently would sometimes give two carousels and no wheel, and the wheel is the shape that says
 * fairground.
 *
 * Fisher-Yates, drawing from the site's own stream so the arrangement is part of the world's identity.
 */
function shuffledCore(random) {
  /*
    One coaster per park, chosen here. Both variants are landmarks and a park wants one landmark of each *kind*,
    so building both would be the one duplication in an otherwise one-of-everything layout — and choosing between
    them is what makes two parks worth visiting.
  */
  const coaster = PARK.COASTER_VARIANTS[Math.floor(random() * PARK.COASTER_VARIANTS.length)];
  const order = [...CORE_FIXED, coaster];

  for (let i = order.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    const swap = order[i];
    order[i] = order[j];
    order[j] = swap;
  }

  return order;
}

/**
 * Whether a park's whole footprint is ground it can stand on.
 *
 * A grid of small probes over the footprint, not one wide query. **A clearance test does not scale by radius** —
 * the water clearance checks a centre and a *ring* at whatever it is given and sees nothing in between, which is
 * how suburbia's streets crossed a lake, how an industrial substation ended up with a corner in one, and how the
 * vineyard's rows did the same. Cheap here in a way it was not for a block grid: there is at most one candidate
 * site to a tile.
 */
function siteIsClear(centreX, centreZ, sampleGroundDetail, isReserved) {
  const probes = PARK.CLEARANCE_PROBES;
  const spacing = (2 * PARK_REACH) / (probes - 1);
  const reach = spacing / 3 + PARK.CLEARANCE;

  for (let i = 0; i < probes; i += 1) {
    for (let j = 0; j < probes; j += 1) {
      const x = centreX - PARK_REACH + spacing * i;
      const z = centreZ - PARK_REACH + spacing * j;

      if (sampleGroundDetail(x, z).slope > PARK.MAX_GROUND_SLOPE) return false;
      if (isReserved?.(x, z, reach)) return false;
    }
  }

  return true;
}

/** Ground, level and low, so a park is a site that was cleared and flattened rather than draped over a hill. */
function createTerrain(seed) {
  const field = createFbm(seed, 'elevation:park', {
    octaves: PARK.OCTAVES,
    baseFrequency: 1 / PARK.WAVELENGTH,
    persistence: PARK.PERSISTENCE,
    lacunarity: PARK.LACUNARITY,
  });

  return function sampleParkElevation(x, z) {
    return toUnitRange(field(x, z)) * PARK.MAX_ELEVATION;
  };
}

/**
 * Which site cells could reach into a span of world.
 *
 * A park is placed from its centre outward, so a site whose centre is well outside a tile can still put a ride
 * inside it. The margin is the park's full reach plus its jitter.
 */
function siteRange(start, length) {
  const spacing = PARK.SITE_SPACING;
  const margin = PARK_REACH + spacing * PARK.SITE_JITTER;

  return {
    first: Math.floor((start - margin) / spacing),
    last: Math.floor((start + length + margin) / spacing),
  };
}

export const park = {
  id: 'park',
  label: 'Fairground',

  palette: PALETTE.park,

  /**
   * The rides get a hot pink ramp where the ground stays a near-black plum.
   *
   * The same trick the forest uses for its trees and industry for its plant, and it matters as much here as
   * anywhere: pink against the city's cyan and industry's amber is what keeps three built regions separable at
   * fog distance.
   */
  structurePalette: PARK_PALETTE,

  suitabilityBias: PARK.SUITABILITY_BIAS,

  /**
   * **The only biome that sets its own region size**, and the reason the override exists.
   *
   * Every other biome is a kind of country that goes on for a while, so a region about a minute across is
   * right for all of them. A fairground is not that. It is one site, and a region sized like a city's is big
   * enough to hold several — which is what was flown and reported: complete fairgrounds side by side, reading
   * as a mistake rather than as a large fairground. See `PARK.FIELD_WAVELENGTH`.
   */
  fieldWavelength: PARK.FIELD_WAVELENGTH,

  /**
   * And it declines the mega scale for the same reason.
   *
   * Part of the world runs at four times the normal region size, which is right for every biome that is a kind of
   * country — a huge mountain range or a huge city is a better mountain range or city. There is no such thing as
   * a bigger fairground. Measured before this, a seed that put a mega patch over a fairground region produced
   * regions 14,000 units across as flown, three times an ordinary biome's, holding one park and otherwise empty.
   */
  scalesWithMega: false,

  /**
   * A fairground takes precedence over another biome's *buildings*, though never over water.
   *
   * **Because it is the only biome in the world that is all-or-nothing.** Every other structural biome is a fill
   * that thins at the edge of its region: an industrial estate losing one block to a neighbour costs it a shed and
   * nothing notices. A fairground refuses its whole site if any part of the footprint is taken, so the same
   * collision costs the entire feature.
   *
   * Measured, and this was the largest single loss in the pipeline: of fifteen sites chosen in a 128,000-unit
   * square, **eleven were refused** — eight by industrial and six by suburbia, which both reserve a whole block
   * wherever they have any weight at all and would have built nothing on most of that ground. Only one involved
   * water.
   *
   * Safe because it changes which of two biomes yields, not whether they overlap: industrial, suburbia and the
   * city all consult the cross-biome reservation when they place, and this biome publishes one, so they keep out
   * of a park from their side. See `isReserved` in `tileContent.js`.
   */
  outranksStructures: true,

  /**
   * Build the elevation function for this biome.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number) => number}
   */
  createElevation(seed) {
    return createTerrain(seed);
  },

  /** Highest elevation this biome can produce, in world units. */
  maxElevation: PARK.MAX_ELEVATION,

  /**
   * Build the structure placement function.
   *
   * @param {number} seed - Base world seed.
   * @returns {(region: object, context: object) => Array<object>}
   */
  createStructures(seed) {
    const siteSeed = deriveSeed(seed, 'sites:park');
    const sites = createSiteFinder(seed);

    /**
     * @param {{originX: number, originZ: number, size: number}} region
     * @param {object} context
     */
    return function collectStructures(
      region,
      { sampleWeight, sampleSuitability, sampleGround, sampleGroundDetail, isReserved },
    ) {
      const structures = [];

      /*
        Lives exactly as long as this tile's build. The peak test reads a neighbourhood around every candidate
        and neighbouring candidates share almost all of it, so this turns a few hundred suitability samples into
        a few dozen. Not held across builds: it would grow without bound as the player flies.
      */
      const siteCache = new Map();

      const xRange = siteRange(region.originX, region.size);
      const zRange = siteRange(region.originZ, region.size);

      const farX = region.originX + region.size;
      const farZ = region.originZ + region.size;

      /** Whether a position belongs to this tile. Owned by the tile containing it, so nothing is drawn twice. */
      const owns = (x, z) => x >= region.originX && x < farX && z >= region.originZ && z < farZ;

      for (let cellX = xRange.first; cellX <= xRange.last; cellX += 1) {
        for (let cellZ = zRange.first; cellZ <= zRange.last; cellZ += 1) {
          /*
            One stream per site, keyed by cell index rather than by tile, so a park does not depend on which tile
            is being built or the order tiles are visited in. Roll order is part of the world's identity —
            append only.
          */
          const random = createLocalRng(siteSeed, cellX, cellZ);

          const [centreX, centreZ] = siteCentre(cellX, cellZ, random);
          const facing = random() * Math.PI * 2;
          const fringeRoll = random();

          /*
            **No per-site roll any more.** A park used to be a site that won a coin toss, which is what let one
            region hold several: the lattice offered two or three sites inside a region and most of them passed.
            Now the region itself chooses one cell, so a park appearing at all is decided by where the regions
            are — which is already random — and the toss would only take parks away from regions entitled to one.
          */
          if (!sites.isCandidate(cellX, cellZ, { sampleWeight, sampleSuitability, cache: siteCache })) {
            continue;
          }

          /*
            **Whole-footprint, and all or nothing.** A park with half its rides missing because one corner was
            steep is the failure this version exists to remove, so the site is either good for a complete
            fairground or it is not used at all.
          */
          if (!siteIsClear(centreX, centreZ, sampleGroundDetail, isReserved)) continue;

          const cos = Math.cos(facing);
          const sin = Math.sin(facing);

          /** A point in the park's own frame, so the whole site turns together. */
          const at = (along, across) => [
            centreX + cos * along - sin * across,
            centreZ + sin * along + cos * across,
          ];

          /** Emit one ride, if this tile owns the ground it stands on. */
          const place = (rideId, x, z) => {
            if (!owns(x, z)) return;

            const shape = getParkShapeOrDefault(rideId);
            const spec = {
              centerX: x,
              centerZ: z,
              baseY: sampleGround(x, z),
              facing,
              sampleBaseY: (px, pz) => sampleGround(px, pz),
            };

            structures.push({
              kind: 'parkRide',
              x,
              z,
              ride: shape.id,
              lines: shape.build(spec),
              solids: shape.masked ? shape.solidsFor(spec) : [],
              site: `${cellX},${cellZ}`,
            });
          };

          // --- the core: one of every ride, shuffled into the slots ---------

          const order = shuffledCore(random);
          const slots = PARK.SLOTS_PER_AXIS;
          const offset = (index) => (index - (slots - 1) / 2) * PARK.SLOT_SIZE;

          for (let i = 0; i < order.length; i += 1) {
            const [x, z] = at(offset(i % slots), offset(Math.floor(i / slots)));
            place(order[i], x, z);
          }

          // --- the fringe: a few small pieces around the edge ---------------

          /*
            Where the randomness lives, and somewhere it cannot strand anything: these ring a park that is
            already complete, so the worst a bad roll can do is put one more arch near a wheel.
          */
          const fringe = countFrom(fringeRoll, PARK.MIN_FRINGE, PARK.MAX_FRINGE);

          for (let i = 0; i < fringe; i += 1) {
            const rideId = PARK.FRINGE_RIDES[Math.floor(random() * PARK.FRINGE_RIDES.length)];

            /*
              Spread around the core on a ring, at a jittered angle. A ring rather than a scatter because the
              distance has to stay outside the core's slots and inside the reach the siting checks covered.
            */
            const angle = ((i + random() * 0.7) / fringe) * Math.PI * 2;
            const distance = CORE_HALF * PARK.FRINGE_REACH;

            const [x, z] = at(Math.cos(angle) * distance, Math.sin(angle) * distance);
            place(rideId, x, z);
          }
        }
      }

      return structures;
    };
  },

  /**
   * Publish the parks so other biomes keep out of them.
   *
   * **A disc per site rather than a rectangle per block**, which is the whole change: the first version reserved
   * the entire region, because the entire region was fairground. Now only the sites are, so the ground between
   * them is available to whatever the neighbouring biomes want to put there.
   *
   * Deliberately a *superset* of the real parks. This has to answer without any terrain — every tree and rock
   * column in the world asks it, from a context that has no ground sampler — so it cannot apply the slope and
   * clearance tests that decide whether a site is really used. Reserving ground where a park *might* stand costs
   * a little open ground and can never let something be built inside one.
   *
   * @returns {(x: number, z: number, radius: number, context: object) => boolean}
   */
  createReservation(seed) {
    const sites = createSiteFinder(seed);

    return function insideAPark(x, z, radius, { sampleWeight, sampleSuitability, cache }) {
      const spacing = PARK.SITE_SPACING;
      const margin = radius + PARK.CLEARANCE + PARK_REACH + spacing * PARK.SITE_JITTER;

      const firstX = Math.floor((x - margin) / spacing);
      const lastX = Math.floor((x + margin) / spacing);
      const firstZ = Math.floor((z - margin) / spacing);
      const lastZ = Math.floor((z + margin) / spacing);

      for (let cellX = firstX; cellX <= lastX; cellX += 1) {
        for (let cellZ = firstZ; cellZ <= lastZ; cellZ += 1) {
          const key = `park:${cellX},${cellZ}`;

          // Memoised per tile, and not optional: every tree and rock column in the world asks this once.
          let site = cache?.get(key);

          if (site === undefined) {
            const [centreX, centreZ] = sites.centreOf(cellX, cellZ);

            site = sites.isCandidate(cellX, cellZ, { sampleWeight, sampleSuitability, cache })
              ? { centreX, centreZ }
              : null;

            cache?.set(key, site);
          }

          if (site === null) continue;

          if (Math.hypot(x - site.centreX, z - site.centreZ) < radius + PARK.CLEARANCE + PARK_REACH) {
            return true;
          }
        }
      }

      return false;
    };
  },
};

/**
 * How far a ride reaches from its own centre, in plan.
 *
 * Exported because the no-overlap guarantee is entirely "every ride fits inside its slot, and slots do not
 * overlap" — so that has to be checkable rather than asserted in a comment.
 */
export function planReachOf(shape) {
  const spec = { centerX: 0, centerZ: 0, baseY: 0, facing: 0 };

  return shape.build(spec).reduce(
    (worst, seg) => Math.max(worst, Math.hypot(seg.x1, seg.z1), Math.hypot(seg.x2, seg.z2)),
    0,
  );
}

/** Exported so tests can reason about a park's layout without restating it. */
export { CORE_HALF as PARK_CORE_HALF, CORE_FIXED, CORE_COUNT };
